import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Field } from "@cntrl/ui";
import { useAuth } from "../../app/auth/useAuth.js";
import { AuthError } from "../../app/auth/session.js";
import { AuthCard } from "./AuthCard.js";
import styles from "./AuthForm.module.scss";

const schema = z
  .object({
    newPassword: z.string().min(12, "Use at least 12 characters"),
    confirm: z.string().min(1, "Confirm your password"),
  })
  .refine((v) => v.newPassword === v.confirm, { message: "Passwords don't match", path: ["confirm"] });
type ResetValues = z.infer<typeof schema>;

export interface ResetPasswordScreenProps {
  /** The single-use token from the reset link (`/reset?token=...`). */
  token: string;
  /** Navigate to /login after a successful reset (the server revoked all sessions). */
  onDone: () => void;
  /** The approved-application link (`&setup=1`): the account is new and has never had a password, so the card
   * reads as setting one up rather than resetting it. Same token, same endpoint. */
  setup?: boolean;
  /** The address of the account this browser is signed in to, if any (ART-315). A reset link can belong to a
   * different account, so the screen says who is signed in and signs them out on save — otherwise the login
   * page would drop the person straight back into the account they did NOT just reset. */
  signedInAs?: string | null;
}

/**
 * Complete a reset BY LINK. Single-use token; on success the server revokes every session, so the
 * user re-authenticates. A missing/absent token renders a graceful error, not a crash.
 */
export function ResetPasswordScreen({ token, onDone, setup = false, signedInAs = null }: ResetPasswordScreenProps) {
  const auth = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({ resolver: zodResolver(schema), mode: "onSubmit" });

  if (!token) {
    return (
      <AuthCard
        title="Link expired"
        subtitle={
          setup
            ? "This setup link is missing or has already been used. Use “Forgot password” on the sign-in page to get a new one."
            : "This reset link is missing or has already been used. Request a new one."
        }
        footer={
          <Button variant="link" type="button" onClick={onDone}>
            Back to sign in
          </Button>
        }
      >
        <span />
      </AuthCard>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await auth.resetPassword({ token, newPassword: values.newPassword });
      if (signedInAs) {
        // Sign this browser out of the OTHER account first; a failed logout must not hide a successful reset.
        try {
          await auth.logout();
        } catch {
          /* the reset already succeeded */
        }
      }
      onDone();
    } catch (err) {
      setServerError(
        err instanceof AuthError && err.status !== 401
          ? err.message
          : "That reset link is no longer valid. Request a new one.",
      );
    }
  });

  return (
    <AuthCard
      title={setup ? "Set up your account" : "Set a new password"}
      subtitle={
        setup
          ? "Choose a password to sign in to Artist CNTRL. Use a strong one you don't use elsewhere."
          : "Choose a strong password you don't use elsewhere."
      }
      // Every other auth card carries a way out; this one shipped without one, stranding anyone who
      // opens a stale link and then wants to retry. Same string as the link-expired state above.
      footer={
        <Button variant="link" type="button" onClick={onDone}>
          Back to sign in
        </Button>
      }
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {signedInAs && (
          <p className={styles.notice}>
            You're signed in as <strong>{signedInAs}</strong>. Saving a new password here signs you out first.
          </p>
        )}
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••••••"
          error={errors.newPassword?.message}
          {...register("newPassword")}
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••••••"
          error={errors.confirm?.message}
          {...register("confirm")}
        />
        {serverError && (
          <p className={styles.serverError} role="alert">
            {serverError}
          </p>
        )}
        <Button variant="accent" type="submit" fullWidth loading={isSubmitting}>
          Set password
        </Button>
      </form>
    </AuthCard>
  );
}
